import React from "react";
import useResourcesByQuery from "commons/hooks/useResourcesByQuery";
import FormSelectField from "commons/components/FormSelectField";
import { Grid } from "@material-ui/core";
import FormFileField from "commons/components/FormFileField";
import FormTextField from "commons/components/FormTextField";
import { FormMoneyField } from "commons/components/FormMoneyField";
import FormSwitch from "commons/components/FormSwitch";
import useTranslate from "commons/hooks/useTranslate";
import ResourceRelations from "commons/components/ResourceRelations";
import { FormNumberField } from "commons/components/FormNumberField";
import { FormDateTimeField } from "commons/components/FormDateField";
import ServiceLocationSelect from "commons/components/ServiceLocationSelect";
import { ComponentManager } from "../products/Editor";

export default function Editor({ current, send, updateModel, extras = [] }) {
  const { model, rules } = current.context;
  const { t } = useTranslate();
  const [categories] = useResourcesByQuery("categories", true);

  return (
    <>
      <Grid item xs={12} sm={3}>
        <FormFileField
          label="image"
          value={model.image}
          onChange={updateModel("image")}
          error={rules.image}
          preview
        />
        <FormSwitch
          label="saleable"
          value={model.saleable}
          onChange={updateModel("saleable")}
        />
        <FormSwitch
          label="purchasable"
          value={model.purchasable}
          onChange={updateModel("purchasable")}
        />
        <FormSwitch
          label="active"
          value={model.active}
          onChange={updateModel("active")}
        />
      </Grid>
      <Grid item xs={12} container sm={9} spacing={2}>
        <FormTextField
          grid={6}
          label="name"
          value={model.name}
          onChange={updateModel("name")}
          error={rules.name}
        />
        <FormTextField
          grid={6}
          label="code"
          value={model.code}
          onChange={updateModel("code")}
          error={rules.code}
        />
        <FormMoneyField
          grid={3}
          label="sale_price"
          value={model.sale_price}
          onChange={updateModel("sale_price")}
          error={rules.sale_price}
        />
        <FormMoneyField
          grid={3}
          label="purchase_price"
          value={model.purchase_price}
          onChange={updateModel("purchase_price")}
          error={rules.purchase_price}
        />
        <FormMoneyField
          grid={3}
          label="cost_price"
          value={model.cost_price}
          onChange={updateModel("cost_price")}
          error={rules.cost_price}
        />
        <FormSelectField
          grid={3}
          options={categories}
          label="category_id"
          value={model.category_id}
          onChange={updateModel("category_id")}
          error={rules.category_id}
        />
        <FormSelectField
          grid={3}
          options={[
            { id: "ONCE", name: t("ONCE") },
            { id: "HOURLY", name: t("HOURLY") },
            { id: "DAILY", name: t("DAILY") },
            { id: "MONTHLY", name: t("MONTHLY") },
            { id: "YEARLY", name: t("YEARLY") },
            { id: "SESSIONS", name: t("SESSIONS") },
            ...extras.map((l) => ({ id: l, name: t(l) })),
          ]}
          label="type"
          value={model.type}
          onChange={updateModel("type")}
          error={rules.type}
        />
        <FormNumberField
          grid={3}
          label={model.type === "SESSIONS" ? "count" : "threshold"}
          value={model.threshold}
          onChange={updateModel("threshold")}
          error={rules.threshold}
          disabled={["ONCE", "CAR_RENTAL", "BALLOON_RIDE"].includes(model.type)}
        />
        <FormDateTimeField
          grid={3}
          label="duration_start"
          value={model.duration_start}
          onChange={updateModel("duration_start")}
          error={rules.duration_start}
          disabled={model.type !== "ONCE"}
        />
        <FormDateTimeField
          grid={3}
          label="duration_end"
          value={model.duration_end}
          onChange={updateModel("duration_end")}
          error={rules.duration_end}
          disabled={model.type !== "ONCE"}
        />
        <FormTextField
          multiline
          label="description"
          value={model.description}
          onChange={updateModel("description")}
        />
        <FacilityManager
          value={model.facilities}
          onChange={updateModel("facilities")}
        />
        <PolicyManager
          value={model.policies}
          onChange={updateModel("policies")}
        />
        {["CAR_RENTAL", "BALLOON_RIDE"].includes(model.type) ? (
          <RideManager
            value={model.locations}
            onChange={updateModel("locations")}
            type={model.type}
          />
        ) : (
          <LocationManager
            value={model.locations}
            onChange={updateModel("locations")}
          />
        )}
        {/* <SessionManager
          locations={model.locations}
          value={model.sessions}
          onChange={updateModel("sessions")}
        /> */}
        <ComponentManager
          product={model.id}
          value={model.components}
          onChange={updateModel("components")}
          ratio_label="service_ratio"
          rules={rules}
        />
      </Grid>
    </>
  );
}

export function FacilityManager({ value, onChange }) {
  const [facilities] = useResourcesByQuery("facilities", true);

  return (
    <ResourceRelations
      title="facilities"
      value={value}
      onChange={onChange}
      model={{
        facility_id: null,
        max_in_sale: 0,
      }}
      rowRenderer={(record, onItemChange) => (
        <>
          <FormSelectField
            grid={6}
            options={facilities}
            label="facility_id"
            value={record.facility_id}
            onChange={onItemChange("facility_id")}
          />
          <FormNumberField
            grid={6}
            label="max_in_sale"
            value={record.max_in_sale}
            onChange={onItemChange("max_in_sale")}
          />
        </>
      )}
    />
  );
}

function PolicyManager({ value, onChange }) {
  const [policies] = useResourcesByQuery("policies", true);

  return (
    <ResourceRelations
      title="policies"
      value={value}
      onChange={onChange}
      model={{
        policy_id: null,
        price: 0,
      }}
      rowRenderer={(record, onItemChange) => (
        <>
          <FormSelectField
            grid={6}
            options={policies}
            label="policy_id"
            value={record.policy_id}
            onChange={onItemChange("policy_id")}
          />
          <FormMoneyField
            grid={6}
            label="price"
            value={record.price}
            onChange={onItemChange("price")}
          />
        </>
      )}
    />
  );
}

export function LocationManager({ value, onChange }) {
  return (
    <ResourceRelations
      title="service-locations"
      value={value}
      onChange={onChange}
      model={{
        service_location_id: null,
        max_allowed: 0,
      }}
      rowRenderer={(record, onItemChange) => (
        <>
          <Grid item xs={12} sm={8}>
            <ServiceLocationSelect
              value={record.service_location_id}
              onChange={onItemChange("service_location_id")}
            />
          </Grid>
          <FormNumberField
            grid={4}
            label="max_allowed"
            value={record.max_allowed}
            onChange={onItemChange("max_allowed")}
          />
        </>
      )}
    />
  );
}

function RideManager({ value, onChange, type }) {
  const t = type === "CAR_RENTAL" ? "CAR" : "BALLOON";
  const label = type === "CAR_RENTAL" ? "car" : "balloon";
  const title = type === "CAR_RENTAL" ? "cars" : "balloons";

  const [balloons] = useResourcesByQuery("service-locations", true, {
    type: t,
  });

  return (
    <ResourceRelations
      title={title}
      value={value}
      onChange={onChange}
      model={{
        service_location_id: null,
        max_allowed: 0,
      }}
      rowRenderer={(record, onItemChange) => (
        <>
          <FormSelectField
            grid={6}
            options={balloons}
            label={label}
            value={record.service_location_id}
            onChange={onItemChange("service_location_id")}
          />
          <FormNumberField
            grid={6}
            label="max_allowed"
            value={record.max_allowed}
            onChange={onItemChange("max_allowed")}
          />
        </>
      )}
    />
  );
}

// function SessionManager({ locations = [], value, onChange }) {
//   const [allLocations] = useResourcesByQuery("service-locations", true);

//   const allowedLocations = useMemo(() => {
//     const ids = locations.map(prop("service_location_id"));
//     return allLocations.filter((loc) => ids.includes(loc.id));
//   }, [allLocations, locations]);

//   return (
//     <ResourceRelations
//       title="sessions"
//       value={value}
//       onChange={onChange}
//       model={{
//         service_location_id: null,
//         starts: null,
//         ends: null,
//       }}
//       rowRenderer={(record, onItemChange) => (
//         <>
//           <FormSelectField
//             grid={6}
//             options={allowedLocations}
//             label="service-location"
//             value={record.service_location_id}
//             onChange={onItemChange("service_location_id")}
//           />
//           <FormDateTimeField
//             grid={3}
//             label="starts"
//             value={record.starts}
//             onChange={onItemChange("starts")}
//           />
//           <FormDateTimeField
//             grid={3}
//             label="ends"
//             value={record.ends}
//             onChange={onItemChange("ends")}
//           />
//         </>
//       )}
//     />
//   );
// }
